/* eslint-disable react/no-danger */
/* eslint-disable react/prop-types */
import React from 'react'
import PropTypes from 'prop-types'
import DOMPurify from 'dompurify'
import ContentLoader from 'react-content-loader'
import { filterProfiles } from 'store/filter/utils'
import { isCampaignProperty, hasActiveCampaign } from 'utils/checkCampaign'
import { useProperty, useLocalityModal, useFilter } from 'store'
import {
  Breadcrumb,
  PropertyBanner,
  CategoryTitle,
  Tag,
  Text,
  Title,
  PropertyFeatures,
  Stage,
  DocumentMiniature,
  DiscountValue,
  PublicCompetitionInfo,
  JuridicalProcess,
  Processes,
} from 'components'
import { PropertyCardContainer } from 'containers'
import getPropertyCategoryImage from 'utils/getPropertyCategoryImage'
import CONSTANTS from 'utils/systemsConstants'

import styles from './PropertyDataContainer.module.scss'

const PropertyDataContainer = ({ id, preview }) => {
  const {
    setCurrentProperty,
    getLoadingProperty,
    getPropertyData,
  } = useProperty()

  const { getClickedBy } = useLocalityModal()
  const clickedBy = getClickedBy()

  const { getSlug } = useFilter()
  const { CONCORRENCIA } = CONSTANTS

  React.useEffect(() => {
    setCurrentProperty(id, preview)
  }, [id, preview]) //eslint-disable-line

  const property = getPropertyData()

  const appraisedLowerThanMain =
    property && property.appraisedPrice < property.mainValue

  const campaignTotalValue =
    property && property.campaignInfo.values.campaignTotalValue

  const isJudicialSale = property && property.saleCategory === 'venda_judicial'
  const isFriendlySale = property && property.saleCategory === 'venda_amigavel'

  const hasPromotion = Boolean(
    campaignTotalValue &&
      property &&
      isCampaignProperty(property.tags) &&
      hasActiveCampaign()
  )
  const sanitizer = DOMPurify.sanitize
  const placeholder = (
    <ContentLoader height={800} className={styles.contentLoader}>
      <rect
        x="0"
        y="20"
        rx="3"
        ry="3"
        width="100%"
        height="10"
        className={styles.breadcrumb}
      />
      <rect x="0" y="40" rx="3" ry="3" width="100%" height="250" />
      {[4, 5, 6, 7, 8, 9, 10].map(i => {
        return (
          <rect
            key={i}
            x="0"
            y={i * 90 - 60}
            rx="3"
            ry="3"
            width="100%"
            height="80"
          />
        )
      })}
    </ContentLoader>
  )

  const typeProperty = name => {
    if (
      name.indexOf('Casa') !== -1 ||
      name.indexOf('casa') !== -1 ||
      name.indexOf('Apartamento') !== -1 ||
      name.indexOf('apartamento') !== -1
    ) {
      return true
    }
    return false
  }

  const buildBreadcrumb = () => {
    const profileName = {
      starter: 'Investidor iniciante',
      experienced: 'Investidor experiente',
      risky: 'Investidor ousado',
      home: 'Não investidor',
    }

    const steps = [
      { name: 'Home', route: '/' },
      {
        name: 'Imóveis',
        route: '/busca',
      },
    ]

    if (clickedBy) {
      steps.push({
        name: profileName[clickedBy],
        route: `/busca/${getSlug() || ''}?${filterProfiles[clickedBy]}`,
      })
    }

    steps.push({
      name: property.propertyName,
      route: `/imovel/${id}`,
    })

    return steps
  }

  return (
    <article className={styles.propertyData}>
      {(getLoadingProperty() || !property) && placeholder}
      {!getLoadingProperty() && property && (
        <>
          <div className={styles.breadcrumb}>
            <Breadcrumb steps={buildBreadcrumb()} blockNavigate={preview} />
          </div>
          <section className={styles.noBorder}>
            <FunctionalPropertyBanner
              propertySmallId={property.propertySmallId}
              address={property.address}
              location={property.location}
              map={property.map}
              imgs={property.images}
              video={property.movie}
              externalTour={property.externalTour}
              internalTour={property.internalTour}
              category={`${property.category}Large`}
            />
          </section>
          {property.isPublicCompetition && property.auctionInfo[0] && (
            <section>
              <PublicCompetitionInfo
                limitDate={property.auctionInfo[0].data}
                limitTime={property.auctionInfo[0].hora}
                video={
                  property.videos[0] && property.videos[0].competition_tutorial
                }
                document={property.document[0] && property.document[0].url}
              />
            </section>
          )}
          <section>
            <CategoryTitle icon="immobile" title="Imóvel" />
            <div className={styles.tags}>
              {isJudicialSale && <Tag key="venda_judicial">Venda Judicial</Tag>}
              {isFriendlySale && <Tag key="venda_amigavel">Venda Amigável</Tag>}
              {property.tags.map((tag, index) => (
                <Tag campaign={tag === CONCORRENCIA} key={index.toString()}>
                  {tag}
                </Tag>
              ))}
            </div>
            <Text className={styles.propertyIdText} color="lightTextColor">
              {property.propertySmallId}
            </Text>
            <Title fontSize="xlarge" type="h1">
              {property.propertyName}
            </Title>
            <Text fontSize="small">{property.address}</Text>
          </section>

          <div className={styles.card}>
            <PropertyCardContainer preview={preview} />
          </div>

          {isJudicialSale && (
            <section>
              <CategoryTitle icon="question" title="Entenda a Venda Judicial" />
              <Text fontSize="medium" color="lightTextColor">
                <p>
                  Uma nova modalidade de venda de imóvel na qual é possível se
                  antecipar à oportunidade e fazer uma proposta de compra antes
                  mesmo do leilão ou do encerramento do processo.
                </p>
                <br />
                <p>
                  <b>Como funciona</b>
                </p>
                <br />
                <p>
                  1. Você faz uma proposta de compra em venda direta ou pode
                  participar de uma disputa digital, quando assim o vendedor
                  determinar.
                </p>
                <p>
                  2. No caso de proposta de compra, o primeiro que a completar
                  com todos os documentos, terá sua proposta encaminhada para
                  aprovação do vendedor do imóvel, mediante o pagamento de
                  caução. No caso de disputa, a proposta ganhadora será
                  encaminhada para aprovação do vendedor, também após o
                  pagamento de caução.{' '}
                </p>
                <p>
                  3. Depois da aprovação do vendedor, a primeira proposta de
                  venda direta ou a proposta ganhadora da disputa digital será
                  encaminhada pelo vendedor do imóvel para apreciação judicial e
                  trâmites finais da liberação do imóvel para venda.{' '}
                </p>
                <p>
                  4. Caso a proposta seja aprovada pelo juiz do processo de
                  venda tem sequência. Caso negada, o valor da caução será
                  devolvido.
                </p>
              </Text>
            </section>
          )}
          {property.juridical && property.juridical.contencioso.length > 0 && (
            <section>
              <CategoryTitle icon="expenses" title="Processo" />
              <Processes litigation={property.juridical.contencioso} />
            </section>
          )}

          {property.features.length > 0 && (
            <section>
              <CategoryTitle icon="characteristics" title="Características" />
              <PropertyFeatures
                isHouseApartment={typeProperty(property.propertyName)}
                allFeatures={property.features}
              />
            </section>
          )}
          {property.description && (
            <section>
              <CategoryTitle icon="about" title="Sobre" />
              <Text fontSize="medium" color="lightTextColor">
                <span
                  className={styles.strong}
                  dangerouslySetInnerHTML={{
                    __html: sanitizer(
                      `<p>${property.description.replace(
                        /(?:\r\n|\r|\n)/g,
                        '<br />'
                      )}</p>`
                    ),
                  }}
                />
              </Text>
            </section>
          )}
          {property.debts.length > 0 && (
            <section>
              <CategoryTitle icon="debts" title="Dívidas" />
              {property.debts.map((debt, index) => (
                <div key={index.toString()}>
                  <Text fontSize="medium" color="lightTextColor">
                    {`${debt.type}: `}
                  </Text>
                  <Text fontSize="medium" color="lightTextColor">
                    {`${`R$ ${debt.value.toLocaleString('pt-br', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}`}`}
                  </Text>
                </div>
              ))}
            </section>
          )}
          {property.considerations && (
            <section>
              <CategoryTitle
                icon="considerations"
                title="Considerações importantes"
              />
              <Text fontSize="medium" color="lightTextColor">
                <span
                  className={styles.strong}
                  dangerouslySetInnerHTML={{
                    __html: sanitizer(
                      `<p>${property.considerations.replace(
                        /(?:\r\n|\r|\n)/g,
                        '<br />'
                      )}</p>`
                    ),
                  }}
                />
              </Text>
            </section>
          )}
          {property.conditions && (
            <section>
              <CategoryTitle title="Formas de Pagamento" icon="payment" />
              {property.conditions.map((condition, index) => (
                <Text
                  key={index.toString()}
                  fontSize="medium"
                  color="lightTextColor"
                >
                  {condition.title}
                </Text>
              ))}
            </section>
          )}
          {!property.hideConditions && !!property.desagio && (
            <section className={styles.discount}>
              <CategoryTitle icon="graphs" title="Valores e descontos" />
              <div className={styles.chart}>
                <DiscountValue
                  discount={
                    (!appraisedLowerThanMain
                      ? property.appraisedPrice
                      : property.mainValue) -
                    (!campaignTotalValue || !hasPromotion
                      ? property.salePrice
                      : campaignTotalValue)
                  }
                  totalValue={property.appraisedPrice}
                  value={
                    !campaignTotalValue || !hasPromotion
                      ? property.mainValue
                      : campaignTotalValue
                  }
                  mainValueLabel={property.mainValueLabel}
                  appraisedLabel={property.appraisedLabel}
                />
              </div>
            </section>
          )}
          {property.expenses.length > 0 && (
            <section>
              <CategoryTitle icon="expenses" title="Despesas" />
              {property.expenses.map((expense, index) => (
                <div key={index.toString()}>
                  <Text fontSize="medium" color="lightTextColor">
                    {`${expense.type}: ${
                      expense.isento
                        ? 'Isento'
                        : `R$ ${expense.value.toLocaleString('pt-br', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}`
                    }`}
                  </Text>
                </div>
              ))}
            </section>
          )}

          {property.stage && (
            <section>
              <CategoryTitle icon="stage" title="Estágio de venda" />
              <div className={styles.stage}>
                <Stage stage={property.stage} />
              </div>
            </section>
          )}

          {property.stage === 'venda_judicial' && (
            <section>
              <CategoryTitle icon="stage" title="Processo jurídico" />
              <div className={styles.stage}>
                <JuridicalProcess
                  processStage={property.juridical.judicialProcessTimeline}
                />
              </div>
            </section>
          )}

          {(property.rgi ||
            property.cityHallRegistration ||
            property.registration) && (
            <section>
              <CategoryTitle icon="data" title="Dados" />
              {!!property.registration && (
                <>
                  <Text fontSize="medium" color="lightTextColor">
                    {`Matrícula: ${property.registration}`}
                  </Text>
                  <br />
                </>
              )}
              {!!property.rgi && (
                <>
                  <Text fontSize="medium" color="lightTextColor">
                    {`RGI: ${property.rgi}`}
                  </Text>
                  <br />
                </>
              )}
              {!!property.cityHallRegistration && (
                <>
                  <Text fontSize="medium" color="lightTextColor">
                    {`Inscrição Prefeitura: ${property.cityHallRegistration}`}
                  </Text>
                  <br />
                </>
              )}
            </section>
          )}
          {!!property.documents.length && (
            <section>
              <CategoryTitle icon="documents" title="Documentos" />
              <div className={styles.docs}>
                {property.documents.map(({ nome, url, type }, index) => (
                  <FunctionalDocumentMiniature
                    key={index.toString()}
                    name={nome}
                    url={url}
                    type={type}
                  />
                ))}
              </div>
            </section>
          )}
          <section>
            <CategoryTitle icon="considerations" title="Aviso legal" />
            <Text fontSize="medium" color="lightTextColor">
              Descrição comercial do imóvel
            </Text>
            <br />
            <Text fontSize="medium" color="lightTextColor">
              A descrição do imóvel é disponibilizada em caráter meramente
              informativo, uma vez que foi criada a partir da matrícula
              apresentada pelo Vendedor e enriquecida com informações do laudo
              de avaliação, documentos que podem sofrer mudanças a qualquer
              momento e não refletir situação atual do imóvel. A Resale não se
              responsabiliza pela veracidade e atualização da descrição do
              imóvel e esclarece que toda e qualquer decisão de compra não deve
              se basear nas fotos divulgadas, mas sim, na realização de visita
              presencial ao imóvel.
            </Text>
            <br />
            <Text fontSize="medium" color="lightTextColor">
              Imagens do Imóvel
            </Text>
            <br />
            <Text fontSize="medium" color="lightTextColor">
              As imagens foram obtidas a partir do laudo de avaliação do imóvel
              e, portanto, podem não refletir sua situação e distribuição
              interna atual. Já as imagens obtidas por meio do Google Street
              View, bem como sua indicação no mapa, são baseadas no endereço
              cadastrado do imóvel e, em alguns casos, pode não ter sua
              localização exata reconhecida pela ferramenta, ou ainda,
              apresentar defasagem com a situação atual, em função da data em
              que foram coletadas pelo Google. Sendo assim, toda e qualquer
              decisão de compra NÃO deve se basear nas fotos divulgadas, mas
              sim, na realização de visita presencial ao imóvel.
            </Text>
            <br />
            <Text fontSize="medium" color="lightTextColor">
              O proponente está ciente de que os documentos e as informações
              fornecidos na proposta serão compartilhados com terceiros tais
              como, órgãos do Poder Judiciário, partes e terceiros envolvidos na
              ação judicial, administradoras de condomínio, tabelionatos de
              notas, cartório de registro de imóveis e prefeituras sempre com a
              finalidade exclusiva de dar cumprimento a esta proposta, bem como
              para viabilização e formalização da alienação judicial do imóvel
              aqui tratado.
            </Text>
          </section>
        </>
      )}
    </article>
  )
}

const FunctionalPropertyBanner = ({
  location,
  imgs,
  video,
  map,
  internalTour,
  externalTour,
  category,
}) => {
  const [selectedItem, setSelectedItem] = React.useState('photos')
  const [carouselIndex, setCarouselIndex] = React.useState(0)

  const selectedItemOnChange = item => {
    setSelectedItem(item)
  }
  const carouselOnChange = value => setCarouselIndex(value)
  const props = {
    bannerIconsProps: {
      selectedItem,
      handleClick: selectedItemOnChange,
    },
    carouselProps: {
      images: imgs,
      onChange: carouselOnChange,
      active: carouselIndex,
      noPhoto: getPropertyCategoryImage(category),
    },
    videoPlayerProps: {
      url: video,
      height: 555,
      width: '100%',
    },
    mapProps: {
      apiKey: 'AIzaSyB53TpACLfJ0UvxXceqjWnCb-4tbenuSs0',
      parameters: {
        q: location,
        center: location,
        zoom: '15',
        mapType: 'roadmap',
      },
    },
    streetViewProps: {
      apiKey: 'AIzaSyB53TpACLfJ0UvxXceqjWnCb-4tbenuSs0',
      parameters: {
        location,
        heading: map.heading,
        pitch: map.pitch,
        fov: '50',
      },
    },
    internalTourProps: {
      url: internalTour,
    },
    externalTourProps: {
      url: externalTour,
    },
  }

  return <PropertyBanner {...props} />
}

const FunctionalDocumentMiniature = ({ name, url, type }) => {
  const [state, setState] = React.useState(false)
  const changeState = () => setState(!state)

  return (
    <DocumentMiniature
      url={url}
      fileName={name}
      type={type}
      parameters={{
        page: 1,
        view: 'fitH',
        toolbar: 0,
        statusbar: 0,
        navpanes: 0,
      }}
      modalProps={{
        opened: state,
        closeModal: changeState,
      }}
    />
  )
}

PropertyDataContainer.propTypes = {
  id: PropTypes.string.isRequired,
  preview: PropTypes.bool,
}

PropertyDataContainer.defaultProps = {
  preview: false,
}
export default PropertyDataContainer
